import { useUser } from './queries/useAuth';
import { registerLocale } from 'react-datepicker';
import enGB from 'date-fns/locale/en-GB';
import enUS from 'date-fns/locale/en-US';

// Track which locales have been registered to prevent duplicate registrations
let localesRegistered = false;

/**
 * Initializes calendar locales for the application
 * This ensures consistent date picker behavior throughout the app
 */
export function initializeCalendarLocales(): void {
  if (localesRegistered) return;

  // Register the locales
  registerLocale('en-GB', enGB); // UK English (Monday first)
  registerLocale('en-US', enUS); // US English (Sunday first)

  localesRegistered = true;
}

// Initialize locales when this module is imported
initializeCalendarLocales();

// Cache for user calendar preferences to avoid recalculations
const userPreferenceCache = new Map<
  string,
  {
    locale: string;
    firstDayOfWeek: 'monday' | 'sunday';
    datePickerLocale: string;
    momentLocale: string;
    firstDayValue: number;
  }
>();

// Map from user-facing first day of week to locale
const firstDayOfWeekToLocale = {
  monday: 'en-GB',
  sunday: 'en-US',
};

// Map from locale to user-facing first day of week
const localeToFirstDayOfWeek = {
  'en-GB': 'monday',
  'en-US': 'sunday',
} as const;

// Function to get first day of week from locale
export const getFirstDayOfWeek = (localeCode: string): 'monday' | 'sunday' => {
  return localeToFirstDayOfWeek[localeCode as keyof typeof localeToFirstDayOfWeek] || 'monday';
};

// Function to get locale from first day of week preference
export const getLocaleFromFirstDayOfWeek = (firstDay: 'monday' | 'sunday'): string => {
  return firstDayOfWeekToLocale[firstDay] || 'en-GB';
};

/**
 * Get user's calendar preferences with caching
 * This avoids recalculating preferences for the same user
 */
const getUserPreferences = (userId: string | undefined, userMetadata: any) => {
  // Return cached result if available
  if (userId && userPreferenceCache.has(userId)) {
    return userPreferenceCache.get(userId)!;
  }

  // Default locale (UK English, Monday first)
  let locale = 'en-GB';

  // Try to get locale from user metadata
  if (userMetadata?.locale) {
    locale = userMetadata.locale;
  } else if (userMetadata?.first_day_of_week) {
    // Handle legacy first_day_of_week property for backward compatibility
    const firstDay = userMetadata.first_day_of_week as 'monday' | 'sunday';
    locale = getLocaleFromFirstDayOfWeek(firstDay);
  }

  // Calculate all preferences
  const preferences = {
    locale,
    firstDayOfWeek: getFirstDayOfWeek(locale),
    datePickerLocale: locale,
    momentLocale: locale === 'en-US' ? 'en' : 'en-gb',
    firstDayValue: locale === 'en-US' ? 0 : 1,
  };

  // Cache result if we have a user ID
  if (userId) {
    userPreferenceCache.set(userId, preferences);
  }

  return preferences;
};

/**
 * Hook to get a user's calendar preferences from their profile
 * Returns the appropriate DatePicker locale and other calendar-related settings
 */
export const useCalendarPreferences = () => {
  const { data: user } = useUser();
  const userId = user?.id;

  // Get preferences (from cache if available)
  const preferences = getUserPreferences(userId, user?.user_metadata);

  return {
    ...preferences,

    // Still include these helper functions
    getLocaleFromFirstDayOfWeek,
    getFirstDayOfWeek,
  };
};
